import styled from '@emotion/styled';
import {BackupShelfItem} from 'electron-communication-lib/BrowserForApp';
import moment from 'moment';
import React, {useCallback} from 'react';
import {useTranslation} from 'react-i18next';
import {
  EDynamicTooltip,
  EIconSmallButton,
  EListEntryComponentProps,
  ETooltip,
} from 'webapp-lib/Components';
import {
  mixinFlexboxColumn,
  mixinFlexboxRow,
  mixinSVGIconSmall,
  mixinTruncate,
} from 'webapp-lib/styling';

import {Delete} from '_/assets/images';

import {showDeleteConfirmationDialog} from './BackupShelf.utils';

export const BackupShelfListComponent = ({
  item,
  onClick,
  onDoubleClick,
  onContextMenu,
}: EListEntryComponentProps<BackupShelfItem>): React.ReactElement => {
  const {t} = useTranslation('documents');
  const handleClick = useCallback(
    (event: React.MouseEvent) => {
      event.stopPropagation();
      onClick(event);
    },
    [onClick]
  );

  const deleteFile = () => showDeleteConfirmationDialog([item]);

  return (
    <StyledItem
      role='button'
      tabIndex={0}
      onClick={handleClick}
      onContextMenu={onContextMenu}
      onDoubleClick={onDoubleClick}
    >
      <RowsWrapper>
        <UpperLine>
          <TextOnly>
            {item.errorState === 'UNKNOWN_FILE'
              ? t('backupShelf.fileList.unknownFile')
              : null}
            {item.errorState === 'UPLOAD_FAILED'
              ? t('backupShelf.fileList.uploadFailed')
              : null}
            {item.errorState === 'VERSION_CONFLICT'
              ? t('backupShelf.fileList.versionConflict')
              : null}
          </TextOnly>
          <IconAdornment onClick={deleteFile}>
            <ETooltip title={t('backupShelf.fileList.deleteFile')}>
              <Delete />
            </ETooltip>
          </IconAdornment>
        </UpperLine>
        <EDynamicTooltip title={item.fileName}>
          <TextOnly>{item.fileName}</TextOnly>
        </EDynamicTooltip>
        <LowerLine>
          <TextOnly>
            {t('backupShelf.fileList.lastModified')}{' '}
            {t('common:dateFormat.withTime', {
              date: moment(item.lastModifiedAt),
            })}
          </TextOnly>
        </LowerLine>
      </RowsWrapper>
    </StyledItem>
  );
};

const UpperLine = styled.div`
  ${mixinFlexboxRow};

  font-weight: var(--font-weight-title);
`;

const LowerLine = styled.div`
  ${mixinFlexboxRow};
  width: 100%;
  justify-content: flex-start;
  align-items: center;
  gap: var(--gutter-tiny);
  svg {
    ${mixinSVGIconSmall};
  }
`;

const TextOnly = styled.div`
  color: var(--color-core-black);
  ${mixinTruncate};
`;

const StyledItem = styled.div`
  padding: var(--gutter-small);

  ${mixinFlexboxRow};
  gap: var(--gutter);
  font-size: var(--font-size-small);
  user-select: none;
  justify-content: flex-start;
  width: 100%;
  border-bottom: var(--regular-border-width) solid var(--color-core-grey);
  cursor: pointer;
  align-items: center;
  color: var(--color-core-black);
`;

const IconAdornment = styled(EIconSmallButton)`
  flex-grow: 0;
  flex-shrink: 0;
  margin-right: var(--gutter-tiny);
  padding: 0;

  & .WrapperEIconButton {
    height: fit-content;
  }
`;

const RowsWrapper = styled.div`
  ${mixinFlexboxColumn};
  gap: var(--gutter-tiny);
  flex-grow: 1;
  overflow: hidden;
`;
